import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MobileNav } from '../components/MobileNav';
import { Ticket, ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

const typeLabel = {
  cashback: 'Cashback 0.2%',
  redeem: 'Redeemed',
  admin_adjustment: 'Admin Adjustment',
};

export const TicketHistory = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [txnsRes, balRes] = await Promise.all([
          axios.get(`${API}/tickets/transactions`, { withCredentials: true }),
          axios.get(`${API}/tickets/balance`, { withCredentials: true })
        ]);
        setTransactions(txnsRes.data);
        setBalance(balRes.data.ticket_balance || 0);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatN = (n) => new Intl.NumberFormat('id-ID').format(Math.abs(n));
  const formatRp = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Ticket History</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(3,32,20,0.4))', border: '1px solid rgba(212,175,55,0.3)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="w-5 h-5" style={{ color: '#D4AF37' }} />
            <span className="text-sm" style={{ color: '#A3B8AC' }}>Current Tickets</span>
          </div>
          <p className="text-4xl font-light" style={{ fontFamily: playfairFont, color: '#D4AF37' }}>
            {formatN(balance)} <span className="text-base" style={{ color: '#A3B8AC' }}>Tickets</span>
          </p>
          <p className="text-sm mt-2" style={{ color: '#A3B8AC' }}>
            Worth {formatRp(balance * 100)} · 1 ticket = Rp 100
          </p>
        </div>

        <div className="rounded-xl p-4" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <p className="text-sm" style={{ color: '#D4AF37' }}>How it works:</p>
          <p className="text-sm mt-1" style={{ color: '#A3B8AC' }}>Earn 0.2% cashback as tickets on every order. Redeem at checkout — each ticket = Rp 100 off.</p>
        </div>

        {loading ? (
          <div className="text-center py-12" style={{ color: '#A3B8AC' }}>Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Ticket className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>No ticket transactions yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((t) => {
              const isPositive = t.amount > 0;
              const Icon = isPositive ? TrendingUp : TrendingDown;
              const color = isPositive ? '#22C55E' : '#EF4444';
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl p-4 backdrop-blur-xl flex items-center gap-4"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`ticket-txn-${t.id}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${color}20`, border: `1px solid ${color}50` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: '#F8FAF9' }}>{typeLabel[t.type] || t.type}</p>
                    <p className="text-xs" style={{ color: '#A3B8AC' }}>{t.description}</p>
                    <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>{new Date(t.created_at).toLocaleString('id-ID')}</p>
                  </div>
                  <p className="text-lg font-semibold" style={{ color, fontFamily: playfairFont }}>
                    {isPositive ? '+' : '-'}{formatN(t.amount)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};
